import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function WhatsappInviteModal({
  open, employee, submitting, error,
  consentChecked, ageChecked, onConsentChange, onAgeChange,
  onConfirm, onClose, gated, gatedMessage,
}) {
  const { t } = useTranslation('team');
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, submitting, onClose]);

  if (!open || !employee) return null;

  const canConfirm = consentChecked && ageChecked;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsapp-invite-title"
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-orange-100 p-6"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center shrink-0">
            <MessageCircle size={18} className="text-green-600" />
          </div>
          <h2 id="whatsapp-invite-title" className="text-base font-semibold text-gray-900">
            {t('whatsapp.confirmTitle')}
          </h2>
        </div>

        <p className="text-sm text-gray-600 leading-snug mb-4">
          {t('whatsapp.confirmBody', { name: employee.fullName })}
        </p>

        <div className="space-y-2.5 mb-4">
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => onConsentChange(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span>{t('whatsapp.consentCheckbox', { name: employee.fullName })}</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={ageChecked}
              onChange={(e) => onAgeChange(e.target.checked)}
              disabled={submitting}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span>{t('whatsapp.ageCheckbox')}</span>
          </label>
        </div>

        {gated && (
          <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
            {gatedMessage}
          </div>
        )}

        {error && (
          <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {t('whatsapp.cancelButton')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || !canConfirm || gated}
            title={gated ? gatedMessage : undefined}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {submitting && <Spinner />}
            {t('whatsapp.confirmButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WhatsappInviteModal;
